(ns app.domain.application.guards.bonds
  (:require [app.domain.application.guards.utils :as guard-utils]
            [app.domain.bond.model :as bond-model]
            [app.domain.util :as du]
            [core.permission.resolver :as permission-resolver]
            [core.util.util :as util]
            [potpuri.core :as p]
            [tilakone.core :as tk]))

(defn- find-bond [fsm]
  (let [bond-id (guard-utils/get-param fsm :bond-id)]
    (-> fsm
        guard-utils/get-domain-group
        bond-model/bonds-in-group
        (p/find-first {:db/id bond-id}))))

(def bond-in-domain-group?
  {:name    :process.guard/bond-in-domain-group?
   :type    :guard
   :summary "Check that the bond in question belongs to the given domain-group"
   :handler (fn [fsm]
              (if (guard-utils/get-param fsm :bond-id)
                (-> (find-bond fsm)
                    :bond/group
                    (= (guard-utils/get-domain-group fsm)))
                ;; skip guard if bond-id parameter not specified, this is just availability check
                true))})

(def bond-in-state?
  {:name    :process.guard/bond-in-state?
   :type    :guard
   :summary "Check the bond's state"
   :handler (fn [{[_ states] ::tk/guard
                  :as        fsm}]
              (if-let [bond (find-bond fsm)]
                (->> bond
                     :domain/state
                     (contains? states))
                true))})

(def bond-owned-by-workspace-party?
  {:name    :process.guard/bond-owned-by-workspace-party?
   :type    :guard
   :summary "Check that the bond owner is a party in the workspace"
   :handler (fn [fsm]
              (if-let [bond (find-bond fsm)]
                (bond-model/bond-owned-by-workspace-party?
                  bond
                  (-> fsm :request :target :entity/parent))
                true))})

(def bond-associated-with-workspace?
  {:name    :process.guard/bond-associated-with-workspace?
   :type    :guard
   :summary "Check that the bond is associated with this specific workspace."
   :handler (fn [fsm]
              (if-let [bond (find-bond fsm)]
                (bond-model/bond-associated-with-workspace?
                  bond
                  (-> fsm :request :target :entity/parent))
                true))})

(def bond-not-associated-with-workspace?
  {:name    :process.guard/bond-not-associated-with-workspace?
   :type    :guard
   :summary (str "Check that the bond is not already associated with this specific workspace. "
                 "It may be associated with another.")
   :handler (fn [fsm]
              (if-let [bond (find-bond fsm)]
                (not (bond-model/bond-associated-with-workspace?
                       bond
                       (-> fsm :request :target :entity/parent)))
                true))})

(def user-has-bond-permission?
  {:name    :process.guard/user-has-bond-permission?
   :type    :guard
   :summary "Check that the user performing the action has specified permission on the bond in question"
   :handler (fn [{[_ required-permission] ::tk/guard
                  user                    :core.user/user
                  :as                     fsm}]
              (if-let [bond (find-bond fsm)]
                (permission-resolver/permitted? bond user required-permission)
                true))})

(defn- bond-requirement-check [workspace allowed-states]
  (or (not (bond-model/bond-required-for-workspace? workspace))
      (and (util/not-empty? (:workspace/bonds workspace))
           (every? (apply du/in-state-fn allowed-states) (:workspace/bonds workspace)))))

(def bond-requirement-is-fulfilled?
  {:name    :process.guard/bond-requirement-is-fulfilled?
   :type    :guard
   :summary "Check that if bond is required by group config, a bond is associated with the workspace"
   :handler (fn [{[_ allow-unverified?] ::tk/guard
                  :as                   fsm}]
              (let [workspace      (-> fsm :request :target :entity/parent)
                    allowed-states (cond-> [:bond.state/verified :bond.state/expired :bond.state/closed]
                                     allow-unverified? (conj :bond.state/unverified))]
                (bond-requirement-check workspace allowed-states)))})

(def verified-bond-requirement-is-fulfilled?
  {:name    :process.guard/verified-bond-requirement-is-fulfilled?
   :type    :guard
   :summary "Check that if bond is required by group config, a verified bond is associated with the workspace. 
             This is for CR issuance as the unverified bond is required in submit."
   :handler (fn [fsm]
              (let [workspace      (-> fsm :request :target :entity/parent)
                    allowed-states [:bond.state/verified]]
                (bond-requirement-check workspace allowed-states)))})

(def guards
  [bond-in-domain-group?
   bond-in-state?
   bond-owned-by-workspace-party?
   bond-associated-with-workspace?
   bond-not-associated-with-workspace?
   user-has-bond-permission?
   bond-requirement-is-fulfilled?
   verified-bond-requirement-is-fulfilled?])
